/**
 * Which cells on this grid a person still has to look at (DOP-479).
 *
 * Read off the table payload, not from a second endpoint: the grid already
 * carries the only signal there is. `cellState` calls a cell `unverified` when
 * extraction found a number but did not re-read the cited source cell — that is
 * exactly "believe this at your own risk", and it is what the legend teaches.
 *
 * What is deliberately NOT here: held values. `kpi_metric_values.held_reason`
 * exists in the database and never reaches `/api/kpi/table`, so a held cell is
 * indistinguishable from an empty one on the wire. Listing empty cells instead
 * would be a different claim — most of them are simply months a document does
 * not cover — so the tab reports what the payload can actually support, and the
 * gap is logged for a backend follow-up rather than guessed at.
 */

import { type KpiTable, cellState } from "../../types/kpi";

export interface KpiReviewItem {
	/** Stable across renders and unique — metric and period identify one cell. */
	id: string;
	metricKey: string;
	metricLabel: string;
	groupLabel: string;
	period: string;
	/** Why it is listed, in the legend's own words. */
	reason: string;
}

const UNVERIFIED_REASON = "Extracted, but not verified against its source.";

/** Every unverified cell on the grid, in row-then-period order. */
export function needsReviewItems(table: KpiTable | null): KpiReviewItem[] {
	if (!table) return [];
	const items: KpiReviewItem[] = [];
	for (const group of table.groups) {
		for (const metric of group.metrics) {
			for (const period of table.periods) {
				if (cellState(metric.cells[period]) !== "unverified") continue;
				items.push({
					id: `${metric.key}@${period}`,
					metricKey: metric.key,
					metricLabel: metric.label,
					groupLabel: group.label,
					period,
					reason: UNVERIFIED_REASON,
				});
			}
		}
	}
	return items;
}
